import { useState } from 'react'
import { chartProgressGradient } from '@/config/chartTheme'
import {
  HardHat,
  AlertTriangle,
  CheckCircle2,
  DollarSign,
  Calendar,
  Bell,
  ChevronRight,
} from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface DrawRequest {
  id: string
  drawNumber: number
  requestDate: string
  amount: number
  status: 'Pending Review' | 'Approved' | 'Funded' | 'Rejected'
  inspector: string
  inspectionDate?: string
  completionPct: number
  notes?: string
}

interface ConstructionLoan {
  id: string
  property: string
  borrower: string
  location: string
  totalCommitment: number
  funded: number
  remaining: number
  ltc: number
  constructionStart: string
  expectedCompletion: string
  completionPct: number
  status: 'On Track' | 'Delayed' | 'Critical'
  draws: DrawRequest[]
  ticklers: Tickler[]
}

interface Tickler {
  id: string
  type: 'Draw Inspection' | 'Budget Reforecast' | 'Insurance Renewal' | 'Permit Renewal' | 'Progress Report' | 'Interest Reserve' | 'Completion Guarantee' | 'Retainage Release'
  dueDate: string
  status: 'Upcoming' | 'Due' | 'Overdue' | 'Completed'
  description: string
  assignee: string
}

/* ------------------------------------------------------------------ */
/*  Mock Data                                                          */
/* ------------------------------------------------------------------ */

const CONSTRUCTION_LOANS: ConstructionLoan[] = [
  {
    id: 'CL-001',
    property: 'The Maxwell at Uptown',
    borrower: 'Crescent Development Partners',
    location: 'Dallas, TX',
    totalCommitment: 48.5,
    funded: 31.2,
    remaining: 17.3,
    ltc: 0.72,
    constructionStart: '2025-03-01',
    expectedCompletion: '2026-09-30',
    completionPct: 64,
    status: 'On Track',
    draws: [
      { id: 'DR-001', drawNumber: 1, requestDate: '2025-04-15', amount: 8.5, status: 'Funded', inspector: 'JLL Construction Consulting', inspectionDate: '2025-04-12', completionPct: 15, notes: 'Foundation & site work complete' },
      { id: 'DR-002', drawNumber: 2, requestDate: '2025-06-20', amount: 6.8, status: 'Funded', inspector: 'JLL Construction Consulting', inspectionDate: '2025-06-18', completionPct: 28 },
      { id: 'DR-003', drawNumber: 3, requestDate: '2025-09-10', amount: 7.2, status: 'Funded', inspector: 'JLL Construction Consulting', inspectionDate: '2025-09-08', completionPct: 42 },
      { id: 'DR-004', drawNumber: 4, requestDate: '2025-12-05', amount: 4.9, status: 'Funded', inspector: 'JLL Construction Consulting', inspectionDate: '2025-12-03', completionPct: 55 },
      { id: 'DR-005', drawNumber: 5, requestDate: '2026-03-01', amount: 3.8, status: 'Approved', inspector: 'JLL Construction Consulting', inspectionDate: '2026-02-28', completionPct: 64 },
      { id: 'DR-006', drawNumber: 6, requestDate: '2026-05-15', amount: 5.5, status: 'Pending Review', inspector: 'TBD', completionPct: 75 },
    ],
    ticklers: [
      { id: 'TK-001', type: 'Draw Inspection', dueDate: '2026-05-10', status: 'Upcoming', description: 'Draw #6 site inspection required before funding', assignee: 'Marcus Webb' },
      { id: 'TK-002', type: 'Budget Reforecast', dueDate: '2026-04-01', status: 'Due', description: 'Q2 budget reforecast due from borrower', assignee: 'Sarah Chen' },
      { id: 'TK-003', type: 'Insurance Renewal', dueDate: '2026-06-15', status: 'Upcoming', description: 'Builder\'s risk insurance renewal', assignee: 'Derek Hoffman' },
      { id: 'TK-004', type: 'Progress Report', dueDate: '2026-03-25', status: 'Overdue', description: 'Monthly progress report & photos', assignee: 'Marcus Webb' },
      { id: 'TK-005', type: 'Interest Reserve', dueDate: '2026-07-01', status: 'Upcoming', description: 'Interest reserve adequacy review', assignee: 'Sarah Chen' },
    ],
  },
  {
    id: 'CL-002',
    property: 'Skyline Industrial Campus Ph. II',
    borrower: 'Pacific Industrial Group',
    location: 'Phoenix, AZ',
    totalCommitment: 35.0,
    funded: 28.7,
    remaining: 6.3,
    ltc: 0.68,
    constructionStart: '2024-11-01',
    expectedCompletion: '2026-06-30',
    completionPct: 82,
    status: 'Delayed',
    draws: [
      { id: 'DR-101', drawNumber: 1, requestDate: '2025-01-15', amount: 10.2, status: 'Funded', inspector: 'Cumming Corp', inspectionDate: '2025-01-12', completionPct: 22 },
      { id: 'DR-102', drawNumber: 2, requestDate: '2025-04-20', amount: 8.5, status: 'Funded', inspector: 'Cumming Corp', inspectionDate: '2025-04-18', completionPct: 45 },
      { id: 'DR-103', drawNumber: 3, requestDate: '2025-08-10', amount: 6.2, status: 'Funded', inspector: 'Cumming Corp', inspectionDate: '2025-08-08', completionPct: 62 },
      { id: 'DR-104', drawNumber: 4, requestDate: '2025-12-15', amount: 3.8, status: 'Funded', inspector: 'Cumming Corp', inspectionDate: '2025-12-12', completionPct: 75 },
    ],
    ticklers: [
      { id: 'TK-101', type: 'Completion Guarantee', dueDate: '2026-06-30', status: 'Upcoming', description: 'Substantial completion deadline per loan agreement', assignee: 'Derek Hoffman' },
      { id: 'TK-102', type: 'Retainage Release', dueDate: '2026-08-01', status: 'Upcoming', description: 'Final retainage release (10%) upon lien waiver receipt', assignee: 'Sarah Chen' },
      { id: 'TK-103', type: 'Permit Renewal', dueDate: '2026-04-15', status: 'Due', description: 'Building permit extension required (Phase II delayed)', assignee: 'Marcus Webb' },
      { id: 'TK-104', type: 'Budget Reforecast', dueDate: '2026-03-20', status: 'Overdue', description: 'Updated cost-to-complete analysis needed due to change orders', assignee: 'Sarah Chen' },
    ],
  },
]

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const fmt = (v: number) => `$${v.toFixed(1)}M`

const fmtDate = (d: string) => new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

const statusColor = (s: string) => {
  const map: Record<string, string> = {
    'On Track': 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20',
    'Delayed': 'text-amber-400 bg-amber-400/10 border-amber-400/20',
    'Critical': 'text-red-400 bg-red-400/10 border-red-400/20',
    'Funded': 'text-emerald-400',
    'Approved': 'text-blue-400',
    'Pending Review': 'text-amber-400',
    'Rejected': 'text-red-400',
    'Upcoming': 'text-blue-400 bg-blue-400/10 border-blue-400/20',
    'Due': 'text-amber-400 bg-amber-400/10 border-amber-400/20',
    'Overdue': 'text-red-400 bg-red-400/10 border-red-400/20',
    'Completed': 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20',
  }
  return map[s] ?? 'text-stone-400 bg-stone-400/10 border-stone-400/20'
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function ConstructionMonitoring({ live }: { live?: boolean }) {
  const [selectedLoan, setSelectedLoan] = useState<string>(CONSTRUCTION_LOANS[0].id)
  const loan = CONSTRUCTION_LOANS.find((l) => l.id === selectedLoan) ?? CONSTRUCTION_LOANS[0]

  if (live) {
    return (
      <div className="panel">
        <div className="panel-body py-10 text-center text-stone-500 text-[12px]">
          No construction-monitoring records are on file for the live book.
        </div>
      </div>
    )
  }

  const totalCommitment = CONSTRUCTION_LOANS.reduce((s, l) => s + l.totalCommitment, 0)
  const totalFunded = CONSTRUCTION_LOANS.reduce((s, l) => s + l.funded, 0)
  const overdueTicklers = CONSTRUCTION_LOANS.flatMap((l) => l.ticklers).filter((t) => t.status === 'Overdue').length
  const dueTicklers = CONSTRUCTION_LOANS.flatMap((l) => l.ticklers).filter((t) => t.status === 'Due').length

  return (
    <div className="space-y-3">
      {/* Summary */}
      <div className="grid grid-cols-4 gap-3">
        <div className="metric-cell flex items-center gap-3">
          <div className="p-1.5 rounded bg-amber-400/10"><HardHat size={14} className="text-amber-400" /></div>
          <div>
            <div className="metric-cell-label">Active Construction Loans</div>
            <div className="metric-cell-value">{CONSTRUCTION_LOANS.length}</div>
          </div>
        </div>
        <div className="metric-cell">
          <div className="metric-cell-label">Total Commitment</div>
          <div className="metric-cell-value">{fmt(totalCommitment)}</div>
        </div>
        <div className="metric-cell">
          <div className="metric-cell-label">Funded / Remaining</div>
          <div className="metric-cell-value">{fmt(totalFunded)} / {fmt(totalCommitment - totalFunded)}</div>
        </div>
        <div className="metric-cell flex items-center gap-3">
          <div className="p-1.5 rounded bg-red-400/10"><Bell size={14} className="text-red-400" /></div>
          <div>
            <div className="metric-cell-label">Ticklers Due / Overdue</div>
            <div className="metric-cell-value">{dueTicklers} / <span className="text-red-400">{overdueTicklers}</span></div>
          </div>
        </div>
      </div>

      {/* Loan Selector */}
      <div className="flex gap-2">
        {CONSTRUCTION_LOANS.map((l) => (
          <button
            key={l.id}
            onClick={() => setSelectedLoan(l.id)}
            className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left transition-all ${
              selectedLoan === l.id
                ? 'border-amber-400/25 bg-amber-400/[0.08]'
                : 'border-stone-700/40 bg-black/20 hover:border-stone-600/50'
            }`}
          >
            <HardHat size={14} className={selectedLoan === l.id ? 'text-amber-300' : 'text-stone-500'} />
            <div>
              <div className="text-[11px] font-medium text-stone-200">{l.property}</div>
              <div className="text-[9px] text-stone-500">{l.id} &middot; {l.completionPct}% complete &middot; {fmt(l.totalCommitment)}</div>
            </div>
            <span className={`ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium border ${statusColor(l.status)}`}>
              {l.status}
            </span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3">
        {/* Draw Schedule */}
        <div className="panel">
          <div className="panel-header">
            <span className="panel-header-title flex items-center gap-1.5">
              <DollarSign size={12} />
              Draw Schedule
            </span>
            <span className="text-[10px] font-mono text-stone-600">{loan.draws.length} draws</span>
          </div>
          <div className="panel-body">
            {/* Progress Bar */}
            <div className="mb-3">
              <div className="flex items-center justify-between text-[10px] mb-1">
                <span className="text-stone-400">Funded: {fmt(loan.funded)} of {fmt(loan.totalCommitment)}</span>
                <span className="font-mono text-amber-300">{loan.completionPct}%</span>
              </div>
              <div className="chart-viz-track chart-viz-bar--rounded h-3 w-full border border-white/10">
                <div
                  className="chart-viz-bar chart-viz-bar--rounded h-full transition-all"
                  style={{ width: `${loan.completionPct}%`, background: chartProgressGradient(1) }}
                />
              </div>
            </div>

            <table className="dense-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th className="text-right">Amount</th>
                  <th>Status</th>
                  <th className="text-right">Completion</th>
                  <th>Inspector</th>
                </tr>
              </thead>
              <tbody>
                {loan.draws.map((dr) => (
                  <tr key={dr.id}>
                    <td className="font-mono text-amber-300">{dr.drawNumber}</td>
                    <td className="text-stone-400 text-[10px]">{fmtDate(dr.requestDate)}</td>
                    <td className="text-right font-mono">{fmt(dr.amount)}</td>
                    <td><span className={`text-[10px] font-medium ${statusColor(dr.status)}`}>{dr.status}</span></td>
                    <td className="text-right font-mono text-stone-300">{dr.completionPct}%</td>
                    <td className="text-stone-500 text-[10px] truncate max-w-[120px]">{dr.inspector}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Tickler System */}
        <div className="panel">
          <div className="panel-header">
            <span className="panel-header-title flex items-center gap-1.5">
              <Bell size={12} />
              Tickler System
            </span>
            <span className="text-[10px] font-mono text-stone-600">
              {loan.ticklers.filter((t) => t.status !== 'Completed').length} active
            </span>
          </div>
          <div className="panel-body space-y-2">
            {loan.ticklers.sort((a, b) => {
              const order = { Overdue: 0, Due: 1, Upcoming: 2, Completed: 3 }
              return (order[a.status] ?? 4) - (order[b.status] ?? 4)
            }).map((t) => (
              <div
                key={t.id}
                className={`rounded-lg border p-2.5 ${
                  t.status === 'Overdue' ? 'border-red-400/20 bg-red-400/[0.03]'
                  : t.status === 'Due' ? 'border-amber-400/15 bg-amber-400/[0.02]'
                  : 'border-stone-700/30 bg-black/20'
                }`}
              >
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium border ${statusColor(t.status)}`}>
                        {t.status === 'Overdue' && <AlertTriangle size={8} className="mr-0.5" />}
                        {t.status === 'Completed' && <CheckCircle2 size={8} className="mr-0.5" />}
                        {t.status}
                      </span>
                      <span className="text-[9px] rounded bg-stone-800 px-1.5 py-0.5 text-stone-400">{t.type}</span>
                    </div>
                    <div className="text-[11px] text-stone-300">{t.description}</div>
                    <div className="flex items-center gap-3 mt-1 text-[9px] text-stone-500">
                      <span className="flex items-center gap-0.5"><Calendar size={9} /> {fmtDate(t.dueDate)}</span>
                      <span>Assigned: {t.assignee}</span>
                    </div>
                  </div>
                  <ChevronRight size={12} className="text-stone-700 mt-1 flex-shrink-0" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
